import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Brand } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

interface ExportRow {
  Name: string;
  Status: string;
}

const columnHelper = createColumnHelper<Brand>();

const RealtorBrands = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const token = getAuthCookie("sessionToken");
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [allCategories, setAllCategories] = useState<{ _id: string; name: string }[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [toastShown, setToastShown] = useState(false);
   const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.brands
  );

    // Fetch all categories for lookup
    useEffect(() => {
      const fetchCategories = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/categories`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          const data = await response.json();
          // console.log("📦 Raw categories API response:", data); // Debug log
          
          if (response.ok) {
            const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            // console.log("📋 Processed categories list:", list); // Debug log
            setAllCategories(list);
          }
        } catch (err) {
          console.error("❌ Failed to fetch categories:", err);
        }
      };
  
      if (token) {
        fetchCategories();
      }
    }, [token]);

      const fetchBrands = async () => {
        setLoading(true);
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/brands/datatable`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              "page": pagination.pageIndex + 1,
              "limit": pagination.pageSize,
              "search": globalFilter,
              "filters": {
                "status": ""
              }, // optionally add { status: statusFilter } if needed
              "sort": {
                "field": "created_at",
                "order": "desc",
              },
              "fields": ["name", "image", "status", "created_at"],
            }),
          });
  
          const result = await response.json();
          setBrands(result.data || []);
          setRowCount(result.total || 0); // make sure backend returns 'total'
        } catch (err) {
          setError(err instanceof Error ? err.message : "Unknown error occurred");
        } finally {
          setLoading(false);
        }
      };
      useEffect(() => {
            fetchBrands();
    }, [pagination, globalFilter, token]);
  
  
    const fetchAllBrands = async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/brands/datatable`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: 1,
          limit: 10000, // assuming this fetches all
          search: globalFilter,
          filters: {},
          sort: {
            field: "created_at",
            order: "desc",
          },
          fields: ["name", "image", "status"],
        }),
      });
      const result = await response.json();
      return result.data || [];
    };
  
  
    useEffect(() => {
      const loadExportData = async () => {
        const allData = await fetchAllBrands();
        const exportData: ExportRow[] = allData.map((row: Brand) => ({
          Name: row.name,
          Status: row.status ? "Active" : "Inactive",
        }));
        setExportRows(exportData);
      };
  
      loadExportData();
    }, []);
  // if (brands?.length === 0) {
  //   return <div>Loading...</div>;
  // }

  const handleDelete = async (brandId: string) => {

    setDeleting(brandId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/brands/${brandId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the brand.");
      }

      showSuccess("Brand deleted successfully!");
      // Update the state by removing the deleted license
      setBrands((prev) =>
        prev?.filter((group) => group.id !== brandId)
      );
      setDeleting(null); // Clear the deleting state
      // navigate(0);
    } catch (err) {
      showError("Failed to delete the brand.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null); // Clear the deleting state
    }
  };
  const handleDeleteClick = (brand: Brand) => {
    setDeleteTarget({
      id: brand.id,
      name: brand.name || "Brand",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };
  const imageUrlMap = usePresignedMap(brands, "image", token);
  const imageCell = useCallback((info: any) => {
    const raw = info.row.original?.image as string | undefined;
    const resolved = getImageSrc(raw, imageUrlMap);
    const src = resolved || imagecustomRounded;
    return (
      <div className="text-sm text-center S-No-2">
        <img
          src={src}
          alt="Brand"
          style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
          }}
        />
      </div>
    );
  }, [imageUrlMap]);
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => (
          <div className="th-text text-center S-No">S.No.</div>
        ),
        cell: (info) => (
          <div className="text-sm text-center S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text text-center S-No-2">Image</div>
        ),
        cell: imageCell,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor(
        (row: Brand) => `${row?.name}`,
        {
          id: "name",
          header: () => (
            <div className="th-text text-center ">Name</div>
          ),
          cell: (info) => (
            <div className="text-sm text-center">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

        columnHelper.accessor(
        (row: any) => row?.categories || [],
        {
          id: "categories",
          header: () => (
            <div className="th-text text-center">Categories</div>
          ),
          cell: (info) => {
            const categoryIds = info.getValue() as string[];
            // console.log("🔍 Category IDs for row:", categoryIds); // Debug log
            // console.log("📚 All categories available:", allCategories); // Debug log
            
            if (!categoryIds || categoryIds.length === 0) {
              return <div className="text-sm text-center text-muted">--</div>;
            }

            // Map IDs to names (handle both _id and id fields)
            const categoryNames = categoryIds
              .map((id) => {
                const cat = allCategories.find((c) => 
                  String(c._id) === String(id) || String((c as any).id) === String(id)
                );
                // console.log(`  🔎 Looking for ID ${id}, found:`, cat); // Debug log
                return cat?.name || null;
              })
              .filter(Boolean);

            // console.log("✅ Mapped category names:", categoryNames); // Debug log

            if (categoryNames.length === 0) {
              return <div className="text-sm text-center text-muted">No categories</div>;
            }

            // Show first 2, rest on hover
            const visible = categoryNames.slice(0, 2);
            const remaining = categoryNames.slice(2);

            return (
              <div className="text-sm text-center">
                <span>{visible.join(", ")}</span>
                {remaining.length > 0 && (
                  <span
                    className="badge bg-primary-subtle text-primary ms-2"
                    style={{ cursor: "help" }}
                    title={remaining.join(", ")}
                  >
                    +{remaining.length} more
                  </span>
                )}
              </div>
            );
          },
          enableGlobalFilter: false,
        }
      ),
      columnHelper.accessor("status", {
        header: () => (
          <div className="th-text text-center">Status</div>
        ),
        cell: (info) => {
          const value = info.getValue() as string | boolean;
          const isActive = value === true || value === "true";

          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm text-center">
              <input
                type="radio"
                className={className}
                checked={isActive}
                readOnly
              />
            </div>
          );
        },
        // cell: (info) => (
        //   <div className="text-sm text-center">
        //     {info.getValue() ? (
        //       <span className="status-badge status-accepted-btn">Active</span>
        //     ) : (
        //       <span className="status-badge status-draft-btn">Inactive</span>
        //     )}
        //   </div>
        // ),
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text text-center">Actions</div>
        ),
        cell: (info) => {
          const categoryInfo = info.row.original;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              {canEdit && (
                <EditIconButton
                  to={`${baseRoute}/editBrand/${categoryInfo.id}`}
                  className="icon-square"
                  title="Edit"
                />
              )}

              {canDelete && (
                <DeleteIconButton
                  onClick={() => handleDeleteClick(categoryInfo)}
                  className="icon-square danger"
                  title="Delete"
                  disabled={deleting === categoryInfo.id}
                />
              )}

            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, imageCell, canView, canEdit, canDelete, allCategories]);

  const table = useReactTable({
    data: brands || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content container">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        {/* <div className="d-flex gap-3 align-items-center">
          <Link to={`${baseRoute}/addBrand`} className="btn btn-primary  table-addbutton">
            + Add Brands
          </Link>
        </div> */}
      </div>
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Brands</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Name"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // :white_check_mark: Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
            {canCreate && (
              <Link to={`${baseRoute}/addBrand`} className="btn btn-primary  table-addbutton">
                + Add Brands
              </Link>
            )}
            {/* <ExportAsExcel
                          data={exportRows}
                          headers={[
                            "Company Name",
                            "First Name",
                            "Last Name",
                            "Phone",
                            "Email",
                            "License Status",
                            "Status",
                          ]}
                          fileName="BrokerageFirms"
                        >
                          {(props) => (
                            <button {...props} className="export-button">
                              <i className="ri-file-excel-2-line me-2" />
                              Export to Excel
                            </button>
                          )}
                        </ExportAsExcel> */}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      )}
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Brand"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default RealtorBrands;
